import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-scroll';
import { useNavigate } from 'react-router-dom';
import { FaBars, FaTimes, FaShoppingBag, FaUser, FaSignOutAlt, FaHistory, FaTachometerAlt } from 'react-icons/fa';
import logo from './logo.png';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import './Navbar.css';

const Navbar = ({ onOpenAuth }) => {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [activeSection, setActiveSection] = useState('home');
  const [profileOpen, setProfileOpen] = useState(false);
  const { itemCount, openCart } = useCart();
  const { user, isLoggedIn, logout, loading } = useAuth();
  const profileRef = useRef(null);
  const navigate = useNavigate();
  const isAdmin = user?.role === 'admin';

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close profile dropdown on outside click
  useEffect(() => {
    const handler = (e) => {
      if (profileRef.current && !profileRef.current.contains(e.target)) {
        setProfileOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const navLinks = [
    { id: 'home', label: 'Home' },
    { id: 'about', label: 'About' },
    { id: 'menu', label: 'Menu' },
    { id: 'gallery', label: 'Gallery' },
    { id: 'contact', label: 'Contact' },
  ];

  const handleLogout = async () => {
    setProfileOpen(false);
    await logout();
  };

  // Get user initials for avatar fallback
  const initials = user?.name
    ? user.name.split(' ').map(n => n[0]).slice(0, 2).join('').toUpperCase()
    : '?';

  return (
    <nav className={`navbar ${scrolled ? 'scrolled' : ''} ${menuOpen ? 'menu-open' : ''}`}>
      <div className="nav-container">
        {/* Logo */}
        <div className="nav-logo">
          <img src={logo} alt="King Kebab Logo" className="logo-img" />
          <div className="logo-text">
            <span className="logo-main">King Kebab</span>
            <span className="logo-sub">Athlone, Co. Westmeath</span>
          </div>
        </div>

        {/* Desktop Links */}
        <ul className="nav-links">
          {navLinks.map((link) => (
            <li key={link.id}>
              <Link
                to={link.id}
                spy={true}
                smooth={true}
                offset={-80}
                duration={600}
                onSetActive={() => setActiveSection(link.id)}
                className={`nav-link ${activeSection === link.id ? 'active' : ''}`}
              >
                {link.label}
              </Link>
            </li>
          ))}
        </ul>

        {/* Right side controls */}
        <div className="nav-right">
          {/* Auth button or User avatar */}
          {!loading && (
            isLoggedIn ? (
              <div className="nav-profile" ref={profileRef}>
                <button
                  className="nav-avatar-btn"
                  onClick={() => setProfileOpen(!profileOpen)}
                  id="nav-avatar-btn"
                  aria-label="User menu"
                >
                  {user?.photo ? (
                    <img src={user.photo} alt={user.name} className="nav-avatar-img" referrerPolicy="no-referrer" />
                  ) : (
                    <span className="nav-avatar-initials">{initials}</span>
                  )}
                </button>

                {/* Dropdown */}
                {profileOpen && (
                  <div className="nav-profile-dropdown">
                    <div className="npd-user">
                      <span className="npd-name">{user?.name}</span>
                      <span className="npd-email">{user?.email}</span>
                    </div>
                    <div className="npd-divider" />
                    {isAdmin && (
                      <button
                        className="npd-item npd-admin"
                        onClick={() => { setProfileOpen(false); navigate('/admin'); }}
                        id="npd-admin-btn"
                      >
                        <FaTachometerAlt /> Admin Dashboard
                      </button>
                    )}
                    <button className="npd-item" id="npd-orders-btn">
                      <FaHistory /> Order History
                    </button>
                    <button className="npd-item npd-logout" onClick={handleLogout} id="npd-logout-btn">
                      <FaSignOutAlt /> Sign Out
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <button
                className="nav-signin-btn"
                onClick={onOpenAuth}
                id="nav-signin-btn"
              >
                <FaUser />
                <span>Sign In</span>
              </button>
            )
          )}

          {/* Order Now CTA */}
          <button
            className="nav-cta"
            onClick={openCart}
            id="nav-order-btn"
            aria-label="Open cart"
          >
            <FaShoppingBag />
            <span>Order Now</span>
            {itemCount > 0 && <span className="nav-cart-badge">{itemCount}</span>}
          </button>

          {/* Mobile Toggle */}
          <button
            className="mobile-toggle"
            onClick={() => setMenuOpen(!menuOpen)}
            aria-label="Toggle menu"
            id="mobile-menu-toggle"
          >
            {menuOpen ? <FaTimes /> : <FaBars />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <div className={`mobile-menu ${menuOpen ? 'open' : ''}`}>
        <ul>
          {navLinks.map((link) => (
            <li key={link.id}>
              <Link
                to={link.id}
                spy={true}
                smooth={true}
                offset={-80}
                duration={600}
                onClick={() => setMenuOpen(false)}
                className="mobile-nav-link"
              >
                {link.label}
              </Link>
            </li>
          ))}
          <li>
            <button
              className="mobile-cta"
              onClick={() => { openCart(); setMenuOpen(false); }}
              id="mobile-order-btn"
            >
              <FaShoppingBag /> Order Now
            </button>
          </li>
          {isLoggedIn && isAdmin && (
            <li>
              <button
                className="mobile-nav-link mobile-admin"
                onClick={() => { navigate('/admin'); setMenuOpen(false); }}
                id="mobile-admin-btn"
              >
                <FaTachometerAlt /> Admin Dashboard
              </button>
            </li>
          )}
          <li>
            {isLoggedIn ? (
              <button className="mobile-nav-link mobile-signout" onClick={handleLogout} id="mobile-signout-btn">
                <FaSignOutAlt /> Sign Out ({user?.name?.split(' ')[0]})
              </button>
            ) : (
              <button
                className="mobile-nav-link mobile-signin"
                onClick={() => { onOpenAuth(); setMenuOpen(false); }}
                id="mobile-signin-btn"
              >
                <FaUser /> Sign In
              </button>
            )}
          </li>
        </ul>
      </div>
    </nav>
  );
};

export default Navbar;
